/**
 * @fileoverview Waveform Audio Player Component
 *
 * Professional audio player using WaveSurfer.js with Redux-based centralized control.
 * Ensures only one audio player is active at a time across the application.
 *
 * **Features:**
 * - Interactive waveform visualization with click-to-seek
 * - Centralized Redux state management (stops other players automatically)
 * - Playback speed control (.5x, .75x, 1x, 1.25x)
 * - Volume control with mute
 * - Play/Pause/Stop controls with terminal aesthetic
 * - Time display (current / duration)
 *
 * @module components/WaveformAudioPlayer
 */

import { useEffect, useRef, useState, useCallback, type ChangeEvent } from 'react'
import WaveSurfer from 'wavesurfer.js'
import { Play, Pause, Square, Volume2, VolumeX } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import {
  playAudio,
  pauseAudio,
  resumeAudio,
  stopAudio,
  setVolume as setGlobalVolume,
  setPlaybackRate as setGlobalPlaybackRate,
  selectCurrentAudioId,
  selectIsPlaying,
  selectVolume,
  selectPlaybackRate,
} from '@/store/slices/globalAudioSlice'

interface WaveformAudioPlayerProps {
  /** Audio URL to play */
  audioUrl: string
  /** Unique identifier for this audio (used for Redux state management) */
  audioId?: string
  /** Optional title to display */
  title?: string
  /** Callback when playback starts */
  onPlay?: () => void
  /** Callback when playback pauses */
  onPause?: () => void
  /** Callback when playback stops */
  onStop?: () => void
}

export function WaveformAudioPlayer({
  audioUrl,
  audioId,
  title,
  onPlay,
  onPause,
  onStop,
}: WaveformAudioPlayerProps) {
  const dispatch = useAppDispatch()
  const waveformRef = useRef<HTMLDivElement>(null)
  const wavesurferRef = useRef<WaveSurfer | null>(null)
  const isInitializedRef = useRef(false)
  const userInitiatedPlayRef = useRef(false) // Track if play was user-initiated to prevent useEffect double-play

  // Use audioUrl as fallback ID if audioId not provided
  const effectiveAudioId = audioId || audioUrl

  // Redux state
  const currentAudioId = useAppSelector(selectCurrentAudioId)
  const globalIsPlaying = useAppSelector(selectIsPlaying)
  const globalVolume = useAppSelector(selectVolume)
  const globalPlaybackRate = useAppSelector(selectPlaybackRate)

  // Check if this player is the active one
  const isThisPlayerActive = currentAudioId === effectiveAudioId
  const isPlaying = isThisPlayerActive && globalIsPlaying

  // Local state
  const [isLoading, setIsLoading] = useState(true)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [hasInteracted, setHasInteracted] = useState(false)

  // Initialize WaveSurfer
  useEffect(() => {
    if (!waveformRef.current || !audioUrl || isInitializedRef.current) return

    let wavesurfer: WaveSurfer | null = null
    let isMounted = true

    // Create WaveSurfer instance
    // CRITICAL FIX: Remove backend: 'WebAudio' - WaveSurfer v7 uses HTML5 audio by default
    // The WebAudio backend option is deprecated and causes silent audio on mobile
    wavesurfer = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: '#4ade80', // terminal-green
      progressColor: '#0ea5e9', // sky-blue
      cursorColor: '#38bdf8',
      barWidth: 2,
      barRadius: 3,
      cursorWidth: 2,
      height: 60, // Reduced from 80 for more compact layout
      barGap: 2,
      normalize: true,
      // backend: 'WebAudio', // ❌ REMOVED - causes no audio on mobile in v7
      mediaControls: false,
    })

    wavesurferRef.current = wavesurfer
    isInitializedRef.current = true

    // Load audio
    wavesurfer.load(audioUrl)

    // Event listeners
    wavesurfer.on('ready', () => {
      if (!isMounted || !wavesurfer) return
      setIsLoading(false)
      setDuration(wavesurfer.getDuration())
      wavesurfer.setVolume(globalVolume)
      wavesurfer.setPlaybackRate(globalPlaybackRate)
    })

    wavesurfer.on('audioprocess', () => {
      if (!isMounted || !wavesurfer) return
      setCurrentTime(wavesurfer.getCurrentTime())
    })

    wavesurfer.on('finish', () => {
      if (!isMounted) return
      dispatch(stopAudio())
      onStop?.()
    })

    // Cleanup
    return () => {
      isMounted = false
      isInitializedRef.current = false
      if (wavesurfer) {
        // Destroy WaveSurfer instance
        try {
          wavesurfer.destroy()
        } catch (error: any) {
          // Ignore abort errors during cleanup (expected in React strict mode)
          if (error?.name !== 'AbortError') {
            console.error('WaveSurfer cleanup error:', error)
          }
        }
      }
      wavesurferRef.current = null
    }
  }, [audioUrl, dispatch, onStop])

  // Sync WaveSurfer playback with Redux state (ONLY for external state changes)
  // NOTE: Direct user clicks should call play()/pause() synchronously in handlePlayPause
  // WARNING: External play() from this useEffect will FAIL on mobile (no user gesture)
  // This is a known limitation - mobile requires user interaction for initial playback
  useEffect(() => {
    if (!wavesurferRef.current || isLoading) return

    // Skip if this was a user-initiated play to prevent double-play
    if (userInitiatedPlayRef.current) {
      userInitiatedPlayRef.current = false
      return
    }

    // Only sync if this player is active and state changed externally (e.g., from another component)
    if (isPlaying && isThisPlayerActive) {
      // This path is for external play commands, not user clicks
      // NOTE: This will fail on mobile if AudioContext hasn't been unlocked by user gesture
      wavesurferRef.current.play().catch((error) => {
        console.warn('[External Play] Failed (likely mobile without user gesture):', error)
        // If play fails, update Redux to reflect reality
        dispatch(pauseAudio())
      })
    } else if (!isPlaying) {
      wavesurferRef.current.pause()
    }
  }, [isPlaying, isThisPlayerActive, isLoading, dispatch])

  // Sync volume changes
  useEffect(() => {
    if (!wavesurferRef.current || isLoading) return
    try {
      wavesurferRef.current.setVolume(globalVolume)
      // Also set on the underlying media element (more reliable)
      const mediaElement = (wavesurferRef.current as any).media
      if (mediaElement) {
        mediaElement.volume = globalVolume
      }
    } catch (error) {
      console.error('Error syncing volume:', error)
    }
  }, [globalVolume, isLoading])

  // Sync playback rate changes
  useEffect(() => {
    if (!wavesurferRef.current || isLoading) return
    wavesurferRef.current.setPlaybackRate(globalPlaybackRate)
  }, [globalPlaybackRate, isLoading])

  // Format time helper
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Control handlers - CRITICAL: NOT async - must call play() IMMEDIATELY for mobile
  const handlePlayPause = useCallback(() => {
    if (isLoading || !wavesurferRef.current) return

    // Mark as interacted on first play
    if (!hasInteracted && !isPlaying) {
      setHasInteracted(true)
    }

    if (isPlaying) {
      // SYNCHRONOUS pause - call directly, then sync Redux
      wavesurferRef.current.pause()
      dispatch(pauseAudio())
      onPause?.()
    } else {
      // CRITICAL MOBILE FIX: Call play() IMMEDIATELY in the click handler
      // This must happen synchronously - NO async/await wrapper
      // Set flag BEFORE calling play() to prevent useEffect race condition
      userInitiatedPlayRef.current = true

      console.log('[Mobile Audio] Calling play() IMMEDIATELY in onClick handler')

      // Call play() and get the promise - but don't await it (that would break mobile)
      const playPromise = wavesurferRef.current.play()

      // Handle the promise result AFTER play() has been called synchronously
      playPromise
        .then(() => {
          console.log('[Mobile Audio] Play succeeded')
          // SUCCESS: Now update Redux to reflect the new state
          try {
            if (isThisPlayerActive) {
              dispatch(resumeAudio())
            } else {
              dispatch(playAudio({ audioUrl, audioId: effectiveAudioId, playerType: 'wavesurfer' }))
            }
            onPlay?.()
          } catch (dispatchError) {
            console.error('[Mobile Audio] Redux dispatch failed:', dispatchError)
            // Audio is playing but Redux update failed - pause to keep state consistent
            wavesurferRef.current?.pause()
            userInitiatedPlayRef.current = false
          }
        })
        .catch((error) => {
          console.error('[Mobile Audio] Play failed:', error)
          // Reset flag if play failed
          userInitiatedPlayRef.current = false
          // Don't update Redux if play failed
        })
    }
  }, [isLoading, isPlaying, isThisPlayerActive, dispatch, audioUrl, effectiveAudioId, onPlay, onPause, hasInteracted])

  const handleStop = useCallback(() => {
    if (!isThisPlayerActive) return

    dispatch(stopAudio())
    wavesurferRef.current?.stop()
    setCurrentTime(0)
    onStop?.()
  }, [isThisPlayerActive, dispatch, onStop])

  const handleVolumeChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value)
    dispatch(setGlobalVolume(newVolume))
    // Apply volume immediately to WaveSurfer instance AND underlying media element
    if (wavesurferRef.current) {
      try {
        wavesurferRef.current.setVolume(newVolume)
        // Also set on the underlying media element (more reliable)
        const mediaElement = (wavesurferRef.current as any).media
        if (mediaElement) {
          mediaElement.volume = newVolume
        }
      } catch (error) {
        console.error('Error setting volume:', error)
      }
    }
  }, [dispatch])

  const toggleMute = useCallback(() => {
    const newVolume = globalVolume === 0 ? 0.7 : 0
    dispatch(setGlobalVolume(newVolume))
    // Apply volume immediately to WaveSurfer instance AND underlying media element
    if (wavesurferRef.current) {
      try {
        wavesurferRef.current.setVolume(newVolume)
        // Also set on the underlying media element (more reliable)
        const mediaElement = (wavesurferRef.current as any).media
        if (mediaElement) {
          mediaElement.volume = newVolume
        }
      } catch (error) {
        console.error('Error toggling mute:', error)
      }
    }
  }, [globalVolume, dispatch])

  const handleSpeedChange = useCallback((rate: number) => {
    dispatch(setGlobalPlaybackRate(rate))
    // Apply playback rate immediately to WaveSurfer instance
    if (wavesurferRef.current) {
      wavesurferRef.current.setPlaybackRate(rate)
    }
  }, [dispatch])

  return (
    <div className="terminal-border border-sky-blue bg-black p-3 sm:p-4">
      {/* Header with title on same line as waveform for compact layout */}
      {title && (
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-sm font-bold text-sky-blue font-mono tracking-wider whitespace-nowrap">
            [AUDIO]
          </h3>
          <div className="text-trail-tan font-mono text-xs flex-1 truncate">{title}</div>
        </div>
      )}

      {/* Waveform Container - Reduced height */}
      <div className="mb-2 bg-black border-2 border-terminal-green/30 rounded p-1">
        <div ref={waveformRef} className="w-full" />
        {isLoading && (
          <div className="text-center py-2 text-sky-blue font-mono text-xs">
            Loading...
          </div>
        )}
      </div>

      {/* Controls Row - Everything on one line for compact layout, centered on mobile */}
      <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap">
        {/* Time Display */}
        <div className="flex items-center gap-1 text-terminal-green font-mono text-xs w-full sm:w-auto justify-center sm:justify-start">
          <span>{formatTime(currentTime)}</span>
          <span className="text-trail-tan">/</span>
          <span>{formatTime(duration)}</span>
        </div>

        {/* Playback Controls */}
        <div className="flex items-center gap-1 w-full sm:w-auto justify-center sm:justify-start">
          {/* Play Button - Green with initial animations */}
          <button
            onClick={handlePlayPause}
            disabled={isLoading || isPlaying}
            className={`p-2 bg-green-500 text-white font-bold border-2 border-white hover:bg-green-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
              !hasInteracted && !isLoading ? 'animate-pulse shadow-[0_0_20px_rgba(34,197,94,0.8)]' : ''
            }`}
            type="button"
            aria-label="Play"
          >
            <Play className={`w-4 h-4 fill-current ${!hasInteracted && !isLoading ? 'animate-bounce' : ''}`} />
          </button>

          {/* Pause Button - Black */}
          <button
            onClick={handlePlayPause}
            disabled={isLoading || !isPlaying}
            className="p-2 bg-black text-white font-bold border-2 border-white hover:bg-gray-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
            aria-label="Pause"
          >
            <Pause className="w-4 h-4" />
          </button>

          {/* Stop Button - Red */}
          <button
            onClick={handleStop}
            disabled={isLoading || !isThisPlayerActive}
            className="p-2 bg-red-500 text-white font-bold border-2 border-white hover:bg-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
            aria-label="Stop"
          >
            <Square className="w-3 h-3 fill-current" />
          </button>
        </div>

        {/* Speed Controls - <<< Speed >>> */}
        <div className="flex items-center gap-1 w-full sm:w-auto justify-center sm:justify-start">
          <button
            onClick={() => handleSpeedChange(Math.max(0.8, globalPlaybackRate - 0.10))}
            disabled={globalPlaybackRate <= 0.8}
            className="px-2 py-1 text-xs font-bold font-mono bg-black text-trail-tan border-2 border-trail-tan hover:border-sky-blue hover:text-sky-blue transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            type="button"
            aria-label="Decrease speed"
          >
            &lt;&lt;&lt;
          </button>
          <div className="px-2 py-1 text-xs font-bold font-mono bg-white text-black border-2 border-white min-w-[3rem] text-center">
            {globalPlaybackRate.toFixed(2)}x
          </div>
          <button
            onClick={() => handleSpeedChange(Math.min(1.2, globalPlaybackRate + 0.10))}
            disabled={globalPlaybackRate >= 1.2}
            className="px-2 py-1 text-xs font-bold font-mono bg-black text-trail-tan border-2 border-trail-tan hover:border-sky-blue hover:text-sky-blue transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            type="button"
            aria-label="Increase speed"
          >
            &gt;&gt;&gt;
          </button>
        </div>

        {/* Volume Control */}
        <div className="flex items-center gap-2 w-full sm:w-auto justify-center sm:justify-start sm:ml-auto">
          <button
            onClick={toggleMute}
            className="text-sky-blue hover:text-terminal-green transition-colors p-1 flex-shrink-0"
            type="button"
            aria-label={globalVolume === 0 ? 'Unmute' : 'Mute'}
          >
            {globalVolume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>

          {/* Volume slider */}
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={globalVolume}
            onChange={handleVolumeChange}
            className="w-20 h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer
              [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
              [&::-webkit-slider-thumb]:bg-terminal-green [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer
              [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:bg-terminal-green
              [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:cursor-pointer"
            style={{
              background: `linear-gradient(to right, #10b981 0%, #10b981 ${globalVolume * 100}%, #374151 ${globalVolume * 100}%, #374151 100%)`
            }}
            aria-label="Volume"
          />

          {/* Volume percentage indicator */}
          <div className="text-terminal-green font-mono text-xs w-8 text-right flex-shrink-0">
            {Math.round(globalVolume * 100)}%
          </div>
        </div>
      </div>
    </div>
  )
}
